import { model, Model, createThunk } from 'mst-collection';
import { getParent, types } from 'mobx-state-tree';
import { formatDistanceToNowStrict, parseJSON } from 'date-fns';

import { IdentifiedPhotoModel } from '../common/IdentifiedPhotoModel';
import { PostUserStatsModel } from './PostUserStatsModel';
import { ThunkAbortController } from '../utils';
import { LikeStatusEnum, Api } from '@api';
import type { Post } from './PostModel';

export class PostData extends Model({
  captionLinkText: types.maybeNull(types.string),
  captionLinkUrl: types.maybeNull(types.string),
  actionText: types.maybeNull(types.string),
  actionUrl: types.maybeNull(types.string),
  text: types.maybeNull(types.string),
  images: types.array(IdentifiedPhotoModel),
  videoSource: types.maybeNull(types.string),
  videoSourceCover: types.maybeNull(types.string),
  likes: types.number,
  comments: types.number,
  publishDate: types.string,
  userStats: PostUserStatsModel,
  isForTrial: types.maybeNull(types.boolean),
}) {
  incrementLikes() {
    this.likes = this.likes + 1;
  }

  decrementLikes() {
    this.likes = this.likes - 1;
  }

  private get _id(): number {
    return getParent<Post>(this, 1).id;
  }

  get formattedPublishDate() {
    return formatDistanceToNowStrict(parseJSON(this.publishDate), {
      addSuffix: true,
    });
  }

  get hasImages() {
    return this.images.length > 0;
  }

  get imagesListPreview() {
    return this.images.map((photo) => ({
      uri: photo.normal?.url,
      thumbnailUri: photo.thumbnail?.url,
    }));
  }

  private _toggleLikeAbortController = new ThunkAbortController();

  // TODO: find a way to make toggleLike reusable (we use the same logic in tutorial)
  toggleLike = createThunk(() => {
    return async function toggleLike(this: PostData) {
      try {
        // Cancel previous request if it in progress
        this._toggleLikeAbortController.abort();

        const newStatus = this.userStats.isLiked
          ? LikeStatusEnum.Unlike
          : LikeStatusEnum.Like;

        if (this.userStats.isLiked) {
          this.decrementLikes();
        } else {
          this.incrementLikes();
        }

        this.userStats.toggleIsLiked();

        await Api.Feed.updateLikeStatus(
          {
            id: this._id,
            status: newStatus,
          },
          this._toggleLikeAbortController.create(),
        );

        this._toggleLikeAbortController.remove();
      } catch (error) {
        // If request was canceled do nothing
        if (ThunkAbortController.isCanceled(error)) {
          return;
        }

        // Change likes count and status back
        if (this.userStats.isLiked) {
          this.decrementLikes();
        } else {
          this.incrementLikes();
        }

        this.userStats.toggleIsLiked();

        throw error;
      }
    };
  });
}

export const PostDataModel = model(PostData);
